import { classnames } from 'Utils/classnames/classnames.utils';
import { Form, useFormikContext } from 'formik';
import React, { Fragment, useEffect } from 'react';
import { t } from 'Src/i18n';
import { useSelector } from 'react-redux';
import { useRouterEvents } from 'Client/router/useRouterEvents';
import { useAppContext } from 'Context/ReactAppContext';
import { Button } from 'Components/frameworks/button/Button';
import { FormikInput as Field } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import type { FormFieldOption } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { ProductQuotationFormKidBirthdate } from 'Components/widgets/product-quotation-form/ProductQuotationFormKidBirthdate';
import {
  DEFAULT_ESCAPADE_MIN_CHILD_AGE,
  DEFAULT_MAX_CHILD_AGE,
  DEFAULT_TOUR_MIN_CHILD_AGE,
} from 'Components/widgets/trip-information/participants/participants.constants';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import type { FormProduct } from './productQuotationForm.utils';
import { FORM_STATUS } from './productQuotationForm.utils';
import { QUOTATION_FORM_ID, RANGE_ESCAPADE } from './productQuotationForm.constants';
type NewsletterOptin = null | {
  title: string;
  description: string;
};
export type ProductQuotationFormProps = {
  adultsOptions: ReadonlyArray<FormFieldOption>;
  ageIndicationAdults: string;
  ageIndicationKids: string;
  availableDatesIndication: string;
  agencies: Array<FormFieldOption>;
  civilityOptions: ReadonlyArray<FormFieldOption>;
  dateFormat: string;
  departureCityDisabled: boolean;
  departureCityOptions: ReadonlyArray<FormFieldOption>;
  departureDateOptions: ReadonlyArray<FormFieldOption>;
  departureDateEnd: Date;
  departureDateStart: Date;
  extensionOptions: ReadonlyArray<FormFieldOption>;
  footnotes: ReadonlyArray<string>;
  hasUser: boolean;
  isProductWithSetDates: boolean;
  isSummerCamp: boolean;
  kidsOptions: ReadonlyArray<FormFieldOption>;
  newsletter: {
    id: string;
    description: string;
  };
  consentOptins: {
    optinEmail: NewsletterOptin;
    optinPhone: NewsletterOptin;
    optinSms: NewsletterOptin;
    optinProfile: NewsletterOptin;
  };
  product: FormProduct;
};
const DEFAULT_KID: {
  birthdate: null | timestamp;
} = {
  birthdate: null,
};

const FormFieldNote = ({ note }: { note: string }) => (
  <span className="mt-5 text-12 text-mediumGrey">{note}</span>
);

export function ProductQuotationForm({
  adultsOptions,
  ageIndicationAdults,
  ageIndicationKids,
  availableDatesIndication,
  agencies,
  civilityOptions,
  dateFormat,
  departureCityDisabled,
  departureCityOptions,
  departureDateOptions,
  departureDateEnd,
  departureDateStart,
  extensionOptions,
  footnotes,
  hasUser,
  isProductWithSetDates,
  isSummerCamp,
  kidsOptions,
  newsletter,
  consentOptins,
  product,
}: ProductQuotationFormProps) {
  const isProspect = useSelector((state: any): boolean => {
    const { userInfo } = state;
    return userInfo?.isProspect;
  });
  const {
    isSubmitting,
    isValid,
    resetForm,
    setValues,
    status,
    values,
    initialValues,
    setFieldValue,
  } = useFormikContext();
  const { transitioning } = useRouterEvents();
  const appContext = useAppContext();
  const isQuotationCommentDisabled = isFeatureFlipped(appContext)('quotation.disableComment');
  const { trackClickEvent } = useTrackEvent();
  const { optinEmail, optinProfile } = consentOptins;
  const [isClient, setIsClient] = React.useState(false);
  useEffect(() => {
    if (transitioning) {
      resetForm();
    }
  }, [transitioning]);
  useEffect(() => {
    setIsClient(true);
  }, []);
  useEffect(() => {
    if (agencies && agencies.length === 1) {
      setFieldValue('traveler.agency', agencies[0].value);
    }
  }, [agencies]);
  const hasLoggedUserNumber = isClient && hasUser && !!initialValues.traveler.gmNumber;
  const isProspectUser = isClient && hasUser && isProspect;
  const { kids = [], kidsNumber = '0' } = values;
  const kidsInt = parseInt(kidsNumber, 10);

  const handleKidsNumber = (e) => {
    const updatedKidsNumber = e.target.value;
    const updatedKidsInt = parseInt(updatedKidsNumber, 10);
    const newKids = updatedKidsInt - kidsInt;
    const updatedKids =
      newKids > 0
        ? [
            ...kids,
            ...Array(newKids)
              .fill(null)
              .map(() => DEFAULT_KID),
          ]
        : kids.slice(0, updatedKidsInt);
    setValues({ ...values, kidsNumber: updatedKidsNumber, kids: updatedKids });
  };

  const defaultMinAuthorizedAge =
    product.rangeId === RANGE_ESCAPADE
      ? DEFAULT_ESCAPADE_MIN_CHILD_AGE
      : DEFAULT_TOUR_MIN_CHILD_AGE;
  const minAuthorizedAgeInMonths = product.minAuthorizedAge.value
    ? product.minAuthorizedAge.value
    : defaultMinAuthorizedAge;
  const maxAuthorizedAgeInMonths = DEFAULT_MAX_CHILD_AGE * 12;
  return (
    <Form id={QUOTATION_FORM_ID} data-testid={QUOTATION_FORM_ID}>
      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('productQuotationForm.criteria.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          {!isProductWithSetDates ? (
            <Field
              className="z-1"
              dataTestId="departure-date-field"
              dateFormat={dateFormat}
              hideErrorMessage
              isRequired
              label={t('productQuotationForm.criteria.dateLabel')}
              maxDate={departureDateEnd}
              minDate={departureDateStart}
              name="departureDate"
              placeholder={t('productQuotationForm.criteria.datePlaceholder')}
              type="datepicker"
            />
          ) : (
            <Fragment>
              <Field
                dataTestId="departure-date-field"
                hideErrorMessage
                isDisabled={departureDateOptions.length === 0}
                isRequired
                label={t('productQuotationForm.criteria.dateLabel')}
                name="departureDate"
                options={departureDateOptions}
                placeholder={t('productQuotationForm.criteria.datePlaceholder')}
                type="select"
              />
              {departureDateOptions.length === 0 && (
                <span className="mt-5 flex items-center gap-5 text-12 text-black">
                  <Icon name={ICONS.WARNING} width=".75rem" />
                  {availableDatesIndication}
                </span>
              )}
            </Fragment>
          )}
        </div>
        {extensionOptions.length > 0 && (
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="extension-field"
              hideErrorMessage
              isRequired
              label={t('productQuotationForm.criteria.extensionLabel')}
              name="extension"
              options={extensionOptions}
              placeholder={t('productQuotationForm.criteria.extensionPlaceholder')}
              type="select"
            />
          </div>
        )}
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="departure-city-field"
            hideErrorMessage
            isDisabled={departureCityDisabled}
            isRequired={!departureCityDisabled}
            label={t('productQuotationForm.criteria.transportationLabel')}
            name="departureCity"
            options={departureCityOptions}
            placeholder={t('productQuotationForm.criteria.transportationPlaceholder')}
            type="select"
          />
        </div>
      </div>
      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('productQuotationForm.pax.title')}
      </Title>
      <div className="flex flex-wrap">
        <p className="mb-0 mt-10 w-full px-10 text-14 font-bold">
          {t('productQuotationForm.pax.paxLabel')}
          {'*'}
        </p>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="adults-number-field"
            hideErrorMessage
            name="adultsNumber"
            options={adultsOptions}
            type="select"
          />
          <FormFieldNote note={ageIndicationAdults} />
        </div>
        {!isSummerCamp && (
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="kids-number-field"
              hideErrorMessage
              name="kidsNumber"
              onChange={handleKidsNumber}
              options={kidsOptions}
              type="select"
            />
            <FormFieldNote note={ageIndicationKids} />
          </div>
        )}
        {kids.length > 0 && (
          <Fragment>
            <div className="w-full p-10 pb-0">
              <p className="text-14 font-bold">
                {t('productQuotationForm.pax.kidsDatesOfBirthLabel')}
              </p>
            </div>
            <div className="flex flex-wrap">
              {kids.map((kid, index) => (
                <ProductQuotationFormKidBirthdate
                  key={`kid-${index}`}
                  dateFormat={dateFormat}
                  index={index}
                  kidsNumber={kids.length}
                  minAuthorizedAgeInMonths={minAuthorizedAgeInMonths}
                  maxAuthorizedAgeInMonths={maxAuthorizedAgeInMonths}
                />
              ))}
            </div>
          </Fragment>
        )}
      </div>
      <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('productQuotationForm.coordinates.title')}
      </Title>
      <div className="flex flex-wrap">
        <p className="mb-0 mt-10 w-full px-10 text-14 font-bold">
          {t('productQuotationForm.coordinates.informationLabel')}
        </p>
        <div className="w-full">
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId="traveler-civility-field"
              hideErrorMessage
              isRequired
              name="traveler.civility"
              options={civilityOptions}
              placeholder={`${t('productQuotationForm.coordinates.civility')} *`}
              type="select"
            />
          </div>
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-firstName-field"
            hideErrorMessage
            isRequired
            name="traveler.firstName"
            placeholder={`${t('productQuotationForm.coordinates.firstName')} *`}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-lastName-field"
            hideErrorMessage
            isRequired
            name="traveler.lastName"
            placeholder={`${t('productQuotationForm.coordinates.lastName')} *`}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-email-field"
            hideErrorMessage
            isRequired
            name="traveler.email"
            placeholder={`${t('productQuotationForm.coordinates.email')} *`}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId="traveler-phone-field"
            hideErrorMessage
            isRequired
            name="traveler.phone"
            placeholder={`${t('productQuotationForm.coordinates.phoneNumber')} *`}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <p className="mb-5 mt-10 w-full px-10 text-14 font-bold">
            {t('productQuotationForm.coordinates.gmNumberLabel')}
          </p>
          <Field
            dataTestId="traveler-gm-number-field"
            hideErrorMessage
            isDisabled={!isClient || hasLoggedUserNumber || isProspectUser}
            name="traveler.gmNumber"
            placeholder={t('productQuotationForm.coordinates.gmNumberPlaceholder')}
            type="text"
          />
        </div>
        {agencies.length > 1 && (
          <div className="w-full p-10 md:w-1/2">
            <p className="mb-5 mt-10 w-full px-10 text-14 font-bold">
              {t('productQuotationForm.coordinates.favoriteTravelAgencyLabel')}
            </p>
            <Field
              dataTestId="traveler-agency-field"
              hideErrorMessage
              isRequired
              name="traveler.agency"
              options={agencies}
              placeholder={`${t('productQuotationForm.coordinates.favoriteTravelAgencyPlaceholder')} *`}
              type="select"
            />
          </div>
        )}
        {optinEmail && (
          <div className="w-full p-10">
            <Field
              className="mb-10"
              dataTestId="optin-email-field"
              label={newsletter.description || optinEmail.title}
              name="optinEmail"
              optimized
              type="checkbox"
            />
            {optinEmail?.description && (
              <Text className="mt-15" text={optinEmail.description} theme="text-sans-n2" />
            )}
            {optinProfile?.description && (
              <Text className="mt-15" text={optinProfile.description} theme="text-sans-n2" />
            )}
          </div>
        )}
      </div>
      {!isQuotationCommentDisabled && (
        <Fragment>
          <Title className="mb-5 mt-30 px-10" hLevel={3} theme="title-sans-n4">
            {t('productQuotationForm.specialRequest.title')}
          </Title>
          <div className="w-full p-10">
            <Field
              dataTestId="comment-field"
              name="comment"
              placeholder={t('productQuotationForm.specialRequest.placeholder')}
              type="textarea"
            />
          </div>
        </Fragment>
      )}
      <div
        className={classnames('mt-30 flex px-10', {
          'text-right': status.state === FORM_STATUS.NEW,
        })}
      >
        {status.state !== FORM_STATUS.NEW && <Confirmation {...status} status={status.state} />}
        <div className="flex-1 text-right">
          <Button
            data-testid="product-quotation-submit"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            onClick={() => {
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_SHOPPING_TOUR_QUOTE, {
                detail_click: 'validate_quote',
              });
            }}
            type="submit"
          >
            {t('productQuotationForm.quotation.buttonLabel')}
          </Button>
          {footnotes.length > 0 && (
            <div className="max-w-full py-10 text-14 leading-normal text-black">
              {footnotes.map((note) => (
                <p className="my-0" key={note}>
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </Form>
  );
}
